import { usePostHog } from 'posthog-js/react';

/**
 * PostHog Event Tracking Helper
 * Provides utility functions for tracking events with user context
 */

/**
 * Get user information from localStorage
 */
export const getUserContext = () => {
  try {
    const userStr = localStorage.getItem('user');
    const userId = localStorage.getItem('userId');
    const email = localStorage.getItem('email');
    const userName = localStorage.getItem('userName');

    const user = userStr ? JSON.parse(userStr) : null;

    return {
      userId: userId || user?.id || 'anonymous',
      email: email || user?.email || 'unknown',
      userName: userName || user?.fullName || 'unknown',
      role: user?.role?.roleName || 'unknown',
      hasActiveSubscription: user?.hasActiveSubscription || false,
      trialDays: user?.trialDays || 0,
    };
  } catch (error) {
    console.error('Error getting user context:', error);
    return {
      userId: 'anonymous',
      email: 'unknown',
      userName: 'unknown',
      role: 'unknown',
      hasActiveSubscription: false,
      trialDays: 0,
    };
  }
};

/**
 * Track API failure event
 */
export const trackApiFailure = (
  posthog: any,
  errorData: {
    url?: string;
    method?: string;
    statusCode?: number;
    errorMessage?: string;
    errorMessages?: any[];
    responseTime?: number;
    requestData?: any;
  }
) => {
  if (!posthog) return;

  const userContext = getUserContext();

  posthog.capture('api_failure', {
    // API Details
    api_url: errorData.url,
    api_method: errorData.method,
    status_code: errorData.statusCode,
    error_message: errorData.errorMessage,
    error_messages: errorData.errorMessages,
    response_time_ms: errorData.responseTime,
    
    // User Context
    user_id: userContext.userId,
    user_email: userContext.email,
    user_name: userContext.userName,
    user_role: userContext.role,
    has_active_subscription: userContext.hasActiveSubscription,
    trial_days_remaining: userContext.trialDays,
    
    // Additional Context
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    page_url: window.location.href,
  });
};

/**
 * Track error page view
 */
export const trackErrorPageView = (
  posthog: any,
  errorCode: number,
  additionalContext?: Record<string, any>
) => {
  if (!posthog) return;

  const userContext = getUserContext();

  posthog.capture('error_page_view', {
    // Error Details
    error_code: errorCode,
    error_type: getErrorType(errorCode),
    
    // User Context
    user_id: userContext.userId,
    user_email: userContext.email,
    user_name: userContext.userName,
    user_role: userContext.role,
    has_active_subscription: userContext.hasActiveSubscription,
    trial_days_remaining: userContext.trialDays,
    
    // Additional Context
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    page_url: window.location.href,
    referrer: document.referrer,
    ...additionalContext,
  });
};

/**
 * Get error type from error code
 */
const getErrorType = (code: number): string => {
  if (code >= 400 && code < 500) return 'client_error';
  if (code >= 500) return 'server_error';
  return 'unknown_error';
};

/**
 * Track runtime error caught by ErrorBoundary
 */
export const trackRuntimeError = (
  posthog: any,
  error: Error,
  additionalContext?: Record<string, any>
) => {
  if (!posthog) return;

  const userContext = getUserContext();

  posthog.capture('runtime_error', {
    // Error Details
    error_name: error?.name || 'Unknown',
    error_message: error?.message || 'No message',
    error_stack: error?.stack?.substring(0, 2000) || 'No stack trace', // Limit stack trace size
    error_type: 'runtime_crash',
    
    // User Context
    user_id: userContext.userId,
    user_email: userContext.email,
    user_name: userContext.userName,
    user_role: userContext.role,
    has_active_subscription: userContext.hasActiveSubscription,
    trial_days_remaining: userContext.trialDays,
    
    // Additional Context
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    page_url: window.location.href,
    referrer: document.referrer,
    ...additionalContext,
  });
};

/**
 * Hook to use PostHog tracking functions
 */
export const usePostHogTracking = () => {
  const posthog = usePostHog();

  return {
    trackApiFailure: (errorData: Parameters<typeof trackApiFailure>[1]) =>
      trackApiFailure(posthog, errorData),
    trackErrorPageView: (errorCode: number, additionalContext?: Record<string, any>) =>
      trackErrorPageView(posthog, errorCode, additionalContext),
    trackRuntimeError: (error: Error, additionalContext?: Record<string, any>) =>
      trackRuntimeError(posthog, error, additionalContext),
  };
};
